<template>
  <div 
    :class="containerClasses"
    role="status"
    :aria-label="message"
  >
    <div class="text-center">
      <!-- Icon -->
      <svg
        :class="iconClasses"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
        v-bind="getIconAttributes()"
      >
        <path :d="getIconPath(icon)" />
      </svg>
      
      <!-- Message -->
      <Text :variant="textVariant" :class="textClasses">
        {{ message }}
      </Text>
      
      <!-- Optional Action Button or Slot -->
      <div v-if="$slots.action || (showAction && actionText)" class="mt-4">
        <slot name="action">
          <Button
            v-if="showAction && actionText"
            @click="handleAction"
            :variant="actionVariant"
            :size="actionSize"
            :disabled="actionDisabled"
          >
            <svg
              v-if="actionIcon"
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
              v-bind="getIconAttributes()"
            >
              <path :d="getIconPath(actionIcon)" />
            </svg>
            {{ actionText }}
          </Button>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Text from './Text.vue'
import Button from './Button.vue'
import { getIconPath, getIconAttributes } from '@/constants/icons'

const props = defineProps({
  icon: {
    type: String,
    default: 'chart'
  },
  message: {
    type: String,
    required: true
  },
  height: {
    type: String,
    default: 'h-[300px]' // h-[250px], h-[300px], h-full, etc.
  },
  iconSize: {
    type: String,
    default: 'w-16 h-16',
    validator: (value) => ['w-12 h-12', 'w-16 h-16', 'w-20 h-20'].includes(value)
  },
  textVariant: {
    type: String,
    default: 'body-sm'
  },
  showAction: {
    type: Boolean,
    default: false
  },
  actionText: {
    type: String,
    default: ''
  },
  actionIcon: {
    type: String,
    default: ''
  },
  actionVariant: {
    type: String,
    default: 'primary'
  },
  actionSize: {
    type: String,
    default: 'md'
  },
  actionDisabled: {
    type: Boolean,
    default: false
  },
  iconColor: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['action'])

const containerClasses = computed(() => {
  return [
    'flex items-center justify-center',
    props.height,
    props.iconColor || 'text-gray-500 dark:text-gray-400'
  ].join(' ')
})

const iconClasses = computed(() => {
  return [
    props.iconSize,
    'mx-auto mb-2 opacity-50'
  ].join(' ')
})

const textClasses = computed(() => {
  return 'text-gray-500 dark:text-gray-400'
})

const handleAction = () => {
  emit('action')
}
</script>
